import { useRouter } from "next/navigation";
import { useEffect, useRef } from "react";

import useGetTokenCT from "./useGetTokenCT";

// Define the types for product attributes
interface Attribute {
  name: string;
  value: string;
}

interface ProductResult {
  masterData: {
    current: {
      masterVariant: {
        attributes: Attribute[];
      };
    };
  };
}

interface ProductResponse {
  results: ProductResult[];
}

// Define the main custom hook function
const useMetaverseRouteChangeHandler = (loaded: boolean) => {
  const hasCTAProcessed = useRef(false);
  const router = useRouter();
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      const { data } = event;
      if (data) {
        let Data: any;
        try {
          Data = typeof data === "object" ? data : JSON.parse(data);
        } catch (error) {
          console.error("Failed to parse data:", error);
          return;
        }

        if (Data.actiontype === "ctaClicked" && !hasCTAProcessed.current) {
          hasCTAProcessed.current = true;
          const emittedUnitName = Data?.unit?.name;

          if (emittedUnitName) {
            const token = await useGetTokenCT();
            if (token) {
              const productDetails = await fetchProductDetails(token, emittedUnitName);
              if (productDetails) {
                const redirectUrl = getRedirectUrl(productDetails);
                router.push(redirectUrl);
              } else {
                console.log("nrict");
              }
            }
          } else {
            console.log("Type is not specified in the URL.");
          }
        }
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  // Helper function to fetch product details using the token
  const fetchProductDetails = async (token: string, unitName: string): Promise<Attribute[] | null> => {
    const apiUrl = `https://api.europe-west1.gcp.commercetools.com/${process.env.NEXT_PUBLIC_CT_PROJECT_KEY}/products/?filter=categories:exists&where=masterData(current(masterVariant(attributes(name%3D%22unitName%22%20and%20value%3D%22${unitName}%22))))`;

    try {
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorDetails = await response.text();
        console.error(`API Error: ${response.status} - ${response.statusText}. Details: ${errorDetails}`);
        throw new Error("Network response was not ok: " + response + response.statusText);
      }

      const data: ProductResponse = await response?.json();
      return data?.results[0]?.masterData?.current?.masterVariant?.attributes;
    } catch (error) {
      console.error("Error fetching product details:", error);
      return null;
    }
  };

  const findObjectAsPerKey = (attributes: any, searchValue: any) => {
    return attributes.find((item: any) => item.name === searchValue);
  };

  // Helper function to generate the redirect URL
  const getRedirectUrl = (attributes: Attribute[]): string => {
    // Extract bedroom number by finding the first part (before any non-numeric characters)
    const bedroomNumber =
      findObjectAsPerKey(attributes, "ipmsBedrooms")?.value.match(/^\d+/)?.[0] || findObjectAsPerKey(attributes, "ipmsBedrooms")?.value;

    // Extract unitName
    const unitName = findObjectAsPerKey(attributes, "unitName")?.value?.split("/").join("-").trim();

    // Property Type - Identifier
    const propertyTypeIdentifier = findObjectAsPerKey(attributes, "identifier")?.value?.toLowerCase().trim();

    // Clean and trim the other attribute
    const bedroomType = bedroomNumber + "-" + propertyTypeIdentifier;

    // Trim spaces and convert the entire string to lowercase
    const cleanBedroomType = bedroomType.trim().replace(/\s+/g, "").toLowerCase();

    return `${window?.location?.href}/properties/${cleanBedroomType}/${unitName}`;
  };
};

export default useMetaverseRouteChangeHandler;
